import { Form } from 'antd';
import { useMemo, useState } from 'react';
import type { TableRowSelection } from 'antd/es/table/interface';
import type { IPatient } from './PatientListSelection';
import { Tag, Tooltip } from 'antd';
import { patientColumns } from './columns';

type UseDataTablePropsResult = {
    searchTerm: string;
    setSearchTerm: (s: string) => void;
    filteredPatients: IPatient[];
    columns: any[];
    pagination: {
        pageSize: number;
        total: number;
        showSizeChanger: boolean;
        onShowSizeChange: (current: number, size: number) => void;
    };
    // we keep rowSelection as `any` to avoid leaking framework-internal types in the exported signature
    rowSelection: any;
};

export default function useDataTableProps(
    data: IPatient[],
    onChange?: any,
    value?: string[],
    form?: any
): UseDataTablePropsResult {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredPatients = useMemo(() => {
        return data?.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [searchTerm, data]);

    const [pageSize, setPageSize] = useState(5);

    const pagination = {
        pageSize,
        total: filteredPatients.length,
        showSizeChanger: true,
        onShowSizeChange: (current: number, size: number) => {
            setPageSize(size);
        },
    };

    // Customize columns for cohort selection - make diagnosis more compact
    const showSourceColumn = Form.useWatch('includeElasticsearchForFilters', form) === true;
    const columns = useMemo(() => {
        const cols = showSourceColumn ? patientColumns : patientColumns.filter(col => col.key !== 'source');
        return cols.map(col => {
            if (col.key === 'diagnosis') {
                return {
                    ...col,
                    width: 250,
                    ellipsis: true,
                    render: (diagnosis: string[]) => {
                        if (!diagnosis || diagnosis.length === 0) return '-';
                        const visibleDiagnoses = diagnosis.slice(0, 2);
                        const remaining = diagnosis.length - 2;

                        const tooltipContent = (
                            <div className="flex max-h-[200px] flex-wrap gap-[4px] overflow-auto text-[#4D4D4D]">
                                {diagnosis.map((d, i) => (
                                    <Tag
                                        key={i}
                                        className="!mr-0"
                                    >
                                        {d}
                                    </Tag>
                                ))}
                            </div>
                        );

                        return (
                            <Tooltip
                                title={tooltipContent}
                                placement="topLeft"
                                color="white"
                                styles={{ body: { boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)' } }}
                            >
                                <div className="flex flex-wrap items-center gap-[4px]">
                                    {visibleDiagnoses.map((d, i) => (
                                        <Tag
                                            key={i}
                                            className="!mr-0 max-w-[100px] truncate"
                                        >
                                            {d}
                                        </Tag>
                                    ))}
                                    {remaining > 0 && <span className="text-xs text-gray-400">+{remaining}</span>}
                                </div>
                            </Tooltip>
                        );
                    },
                };
            }
            if (col.key === 'ethnicity' || col.key === 'race') {
                return {
                    ...col,
                    width: 120,
                };
            }
            return col;
        });
    }, [showSourceColumn]);

    const rowSelection: TableRowSelection<IPatient> = {
        type: 'checkbox',
        selectedRowKeys: value,

        onSelectAll: (selected: boolean, selectedRows: IPatient[], changeRows: IPatient[]) => {
            const allKeys = data.map(d => String(d.id));
            if (selected) {
                onChange?.([...new Set([...(value ?? []), ...allKeys])]);
            } else {
                const newValue = value?.filter(v => !allKeys.includes(v));
                onChange?.(newValue);
            }
        },

        onSelect: (record: IPatient, selected: boolean, selectedRows: IPatient[]) => {
            // Keep parent in sync with the selectedRows after a single-row toggle

            let updatedValue = [...(value ?? [])];

            if (value?.includes(record?.id)) {
                updatedValue = updatedValue.filter(u => u !== record?.id);
            } else {
                updatedValue.push(record?.id);
            }
            onChange?.(updatedValue);
        },
    };

    return { searchTerm, setSearchTerm, filteredPatients, columns, pagination, rowSelection };
}
